import React, { useState } from 'react';
import { db } from '../firebase/config';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import {
  Mail,
  Phone,
  MapPin,
  CheckCircle
} from 'lucide-react';
import CSRFToken from '../security/CSRFToken';
import { sanitizeText, validateEmail, sanitizeObject } from '../security/sanitize';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [csrfToken, setCsrfToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Sanitize input as it's entered
    const sanitizedValue = sanitizeText(value);
    setFormData({
      ...formData,
      [name]: sanitizedValue
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    // Validate CSRF token
    const submittedToken = e.target.elements.csrfToken.value;
    const storedToken = sessionStorage.getItem('csrfToken');
    
    if (submittedToken !== storedToken) {
      setError('Security validation failed. Please refresh the page and try again.');
      setIsSubmitting(false);
      return;
    }
    
    // Validate email
    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address.');
      setIsSubmitting(false);
      return;
    }
    
    try {
      // Sanitize all form data before submission
      const sanitizedData = sanitizeObject(formData);
      
      // Save the contact form submission to Firestore
      const contactsCollection = collection(db, 'contactSubmissions');
      
      const docRef = await addDoc(contactsCollection, {
        name: sanitizedData.name,
        email: sanitizedData.email,
        company: sanitizedData.company || '',
        phone: sanitizedData.phone || '',
        message: sanitizedData.message,
        recipientEmail: 'hola@engagedmanila.com',
        status: 'new',
        createdAt: serverTimestamp(),
        read: false,
        csrfToken: submittedToken // Store the token for audit purposes
      });
      
      // Form submission saved successfully
      
      // Call the HTTP Cloud Function to send the email
      const functionUrl = `https://us-central1-engagedmanila-aa4b0.cloudfunctions.net/sendContactFormEmailV2?id=${docRef.id}`;
      
      try {
        const response = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          // Email sent successfully
        } else {
          console.error('Error sending email:', await response.text());
          // The form submission is still considered successful even if email sending fails
        }
      } catch (emailError) {
        console.error('Error calling email function:', emailError);
        // The form submission is still considered successful even if email sending fails
      }
      
      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: ''
      });
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('There was an error submitting your message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50">
      <SEO
        title="Contact Us — Engaged Manila"
        description="Get in touch with Engaged Manila. Reach out for brand partnerships, influencer support, or general inquiries about our influencer marketing platform."
        url="/contact"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          {/* Background decorative elements */}
          <div className="absolute top-1/4 right-0 w-64 h-64 bg-blue-100 rounded-full opacity-20 transform translate-x-1/2"></div>
          <div className="absolute bottom-1/4 left-0 w-80 h-80 bg-primary rounded-full opacity-10 transform -translate-x-1/2"></div>
          <div className="absolute top-1/3 left-1/4 w-40 h-40 bg-blue-200 rounded-full opacity-20"></div>
          
          {/* Grid pattern */}
          <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-gray-900 tracking-tight">
              Get in <span className="text-primary">Touch</span>
            </h1>
            <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-500">
              Have questions about our platform? Our team is here to help you get started.
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Contact Form Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Contact Information */}
              <div className="bg-primary p-10 text-white">
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  className="h-full flex flex-col justify-between"
                >
                  <div>
                    <motion.h2 
                      variants={itemVariants}
                      className="text-3xl font-bold mb-6"
                    >
                      Contact Information
                    </motion.h2>
                    <motion.p 
                      variants={itemVariants}
                      className="text-blue-100 max-w-sm mb-12"
                    >
                      Fill out the form and our team will get back to you within 24 hours.
                    </motion.p>
                    
                    <motion.div variants={itemVariants} className="flex items-center mt-8">
                      <div className="flex-shrink-0">
                        <Mail className="h-6 w-6 text-blue-200" />
                      </div>
                      <div className="ml-4 text-blue-100">
                        <p>hola@engagedmanila.com</p>
                      </div>
                    </motion.div>
                    

                    
                    <motion.div variants={itemVariants} className="flex items-center mt-6">
                      <div className="flex-shrink-0">
                        <MapPin className="h-6 w-6 text-blue-200" />
                      </div>
                      <div className="ml-4 text-blue-100">
                        <p>785 Aurora Boulevard, Quezon City</p>
                        <p>Metro Manila, Philippines</p>
                      </div>
                    </motion.div>
                  </div>
                  
                  <motion.div variants={itemVariants} className="mt-12">
                    <p className="text-sm text-blue-200">
                      © {new Date().getFullYear()} Engaged Manila. All rights reserved.
                    </p>
                  </motion.div>
                </motion.div>
              </div>
              
              {/* Contact Form */}
              <div className="p-10">
                {submitted ? (
                  <motion.div 
                    className="h-full flex flex-col items-center justify-center text-center"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <CheckCircle className="h-16 w-16 text-green-500 mb-6" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank you!</h2>
                    <p className="text-gray-600 max-w-sm">
                      Your message has been sent successfully. We'll get back to you as soon as possible.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="mt-8 px-6 py-3 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors duration-300"
                    >
                      Send another message
                    </button>
                  </motion.div>
                ) : (
                  <motion.form 
                    onSubmit={handleSubmit}
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="space-y-6"
                  >
                    <CSRFToken onTokenGenerated={setCsrfToken} />
                    <motion.h2 
                      variants={itemVariants}
                      className="text-3xl font-bold text-gray-900 mb-8"
                    >
                      Send us a message
                    </motion.h2>
                    
                    {error && (
                      <motion.div
                        variants={itemVariants}
                        className="bg-red-50 border-l-4 border-red-400 p-4 mb-6"
                      >
                        <p className="text-red-700">{error}</p>
                      </motion.div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <motion.div variants={itemVariants}>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                          placeholder="Juan Dela Cruz"
                        />
                      </motion.div>
                      
                      <motion.div variants={itemVariants}>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                          Email *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                          placeholder="juan@example.com"
                        />
                      </motion.div>
                      
                      <motion.div variants={itemVariants}>
                        <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                          Company
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                          placeholder="Your Company"
                        />
                      </motion.div>
                      
                      <motion.div variants={itemVariants}>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                          placeholder="+63 XXX XXX XXXX"
                        />
                      </motion.div>
                    </div>
                    
                    <motion.div variants={itemVariants}>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                        placeholder="How can we help you?"
                      ></textarea>
                    </motion.div>
                    
                    <motion.div variants={itemVariants}>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`w-full px-6 py-3 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors duration-300 flex justify-center items-center ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
                      >
                        {isSubmitting ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Sending...
                          </>
                        ) : (
                          'Send Message'
                        )}
                      </button>
                    </motion.div>
                  </motion.form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-5xl mx-auto">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold text-gray-900">Frequently Asked Questions</h2>
            <p className="mt-4 text-gray-500">
              Find quick answers to common questions about our platform.
            </p>
          </motion.div>
          
          <motion.div 
            className="grid gap-6 md:grid-cols-2"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {[
              {
                question: "How do I create a campaign?",
                answer: "Once you've registered as a brand, you can create a campaign from your dashboard by clicking on 'Create Campaign' and following the step-by-step process."
              },
              {
                question: "What types of influencers are on your platform?",
                answer: "Our platform hosts influencers across various niches including fashion, beauty, lifestyle, technology, food, fitness, and more, with follower counts ranging from micro to macro influencers."
              },
              {
                question: "How does the payment process work?",
                answer: "Payments are securely processed through our platform. Brands deposit funds when creating campaigns, and influencers are paid after successful completion and approval of their content."
              },
              {
                question: "Can I target specific demographics for my campaign?",
                answer: "Yes, our platform allows you to specify target demographics including age range, location, interests, and more when setting up your campaign."
              }
            ].map((faq, index) => (
              <motion.div 
                key={index}
                variants={itemVariants}
                className="bg-white p-6 rounded-lg shadow-md"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Contact;
